import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import {
  multiFactor,
  sendEmailVerification,
  reauthenticateWithCredential,
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  type TotpSecret,
} from "firebase/auth";
import { QRCodeSVG } from "qrcode.react";
import { friendlyAuthError, useAuth } from "@/lib/auth";
import { authSettled, getFirebase } from "@/lib/firebase";
import { completeMfaEnrolment, enrolView, type EnrolView } from "@/lib/mfa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/mfa-enroll")({
  // Mirror of the _authed guard: it sends accounts with NO factor here, this
  // sends accounts that already have one away. Enrolling a second device is
  // deliberately unsupported: minting a secret needs a recent sign-in, and a
  // password-only re-auth can never satisfy an MFA account — an enrolled admin
  // who opened this page by hand got a password prompt and never a QR
  // (08/09/2026).
  beforeLoad: async () => {
    const user = await authSettled(getFirebase().auth);
    if (user && multiFactor(user).enrolledFactors.length > 0) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: MfaEnrollPage,
});

interface EnrollError {
  code?: string;
  message: string;
  raw?: unknown;
}

function MfaEnrollPage() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<EnrollError | null>(null);
  const [busy, setBusy] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  // Kept apart from `error`: that one selects the card, and a failed resend
  // must not unmount the card holding the Resend and Sign out buttons.
  const [resendError, setResendError] = useState<string | null>(null);
  const [showUri, setShowUri] = useState(false);
  const [uriCopied, setUriCopied] = useState(false);
  const [reauthPassword, setReauthPassword] = useState("");
  // Stays true across a wrong password, so the form does not vanish when the
  // re-auth error replaces the requires-recent-login error it was keyed on.
  const [needsReauth, setNeedsReauth] = useState(false);

  // Enrolling a second factor is security-sensitive, so Firebase demands a
  // recent sign-in and answers auth/requires-recent-login otherwise. Rather
  // than dead-end an admin who has been signed in a while — which is exactly
  // the admin most likely to be enrolling under pressure — the page offers to
  // re-authenticate in place and retries.
  const generate = useCallback(async () => {
    if (!user) {
      setNeedsReauth(false);
      setError(null);
      return;
    }
    try {
      const session = await multiFactor(user).getSession();
      const generated = await TotpMultiFactorGenerator.generateSecret(session);
      setSecret(generated);
      setError(null);
      setNeedsReauth(false);
    } catch (err) {
      const e = err as { code?: string; message?: string };
      console.error("MFA enrol: generateSecret failed", err);
      if (e.code === "auth/requires-recent-login") {
        setNeedsReauth(true);
        setError(null);
        return;
      }
      setNeedsReauth(false);
      setError({ code: e.code, message: e.message ?? "Could not generate TOTP secret.", raw: err });
    }
  }, [user]);

  useEffect(() => {
    void generate();
  }, [generate]);

  async function reauthenticate() {
    if (!user?.email || busy || !reauthPassword) return;
    setBusy(true);
    try {
      await reauthenticateWithCredential(
        user,
        EmailAuthProvider.credential(user.email, reauthPassword),
      );
      setReauthPassword("");
      await generate();
    } catch (err) {
      const e = err as { code?: string; message?: string };
      setError({
        code: e.code,
        message:
          e.code === "auth/wrong-password" || e.code === "auth/invalid-credential" ?
            "That password is not correct." :
            friendlyAuthError(e.code, e.message ?? "Could not confirm your password."),
        raw: err,
      });
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!user || !secret) return;
    setBusy(true);
    setError(null);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(user).enroll(assertion, "Authenticator app");
      // The current token has no second-factor claim; admin callables would
      // reject it. Re-sign-in is the only way to get one (see lib/mfa.ts).
      await completeMfaEnrolment({ signOut, navigate });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      console.error("MFA enrol: verify failed", err);
      if (e.code === "auth/requires-recent-login") {
        // The session aged between generating the secret and enrolling it.
        // Drop the secret so the re-auth form takes over; a fresh one is
        // generated after the password is confirmed.
        setSecret(null);
        setNeedsReauth(true);
        setError(null);
        return;
      }
      setError({ code: e.code, message: e.message ?? "Could not verify code.", raw: err });
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification() {
    if (!user) return;
    setVerificationSending(true);
    setResendError(null);
    try {
      await sendEmailVerification(user);
      setVerificationSent(true);
    } catch (err) {
      console.error("sendEmailVerification failed", err);
      setResendError((err as { message?: string }).message ?? "Could not send verification email.");
    } finally {
      setVerificationSending(false);
    }
  }

  const otpauthUri = secret ?
    secret.generateQrCodeUrl(user?.email ?? "PinStaQ Admin", "PinStaQ Admin") :
    "";
  const view = enrolView({
    loading,
    signedIn: user !== null,
    secretReady: secret !== null,
    error,
    needsReauth,
  });

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6 rounded-xl border bg-card p-8 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">Enrol two-factor authentication</h1>
          <p className="text-sm text-muted-foreground">{SUBTITLE[view]}</p>
        </div>

        {view === "ready" ? (
          <div className="space-y-3">
            <div className="flex justify-center rounded-md border bg-white p-4">
              <QRCodeSVG value={otpauthUri} size={192} level="M" includeMargin={false} />
            </div>
            <p className="text-center text-xs text-muted-foreground">
              Scan with Google Authenticator, 1Password, Authy, or any TOTP app.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowUri((v) => !v)}
              >
                {showUri ? "Hide setup key" : "Can't scan? Show setup key"}
              </Button>
              {showUri ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(otpauthUri);
                    setUriCopied(true);
                    window.setTimeout(() => setUriCopied(false), 2000);
                  }}
                >
                  {uriCopied ? "Copied" : "Copy URI"}
                </Button>
              ) : null}
            </div>
            {showUri ? (
              <div className="break-all rounded-md bg-muted p-3 text-xs font-mono">
                {otpauthUri}
              </div>
            ) : null}
            {error ? <ErrorBox title="Could not verify that code" error={error} /> : null}
          </div>
        ) : view === "unverified-email" ? (
          <UnverifiedEmailCard
            email={user?.email ?? ""}
            verificationSent={verificationSent}
            verificationSending={verificationSending}
            resendError={resendError}
            onResend={() => void resendVerification()}
            onSignOut={() => void signOut()}
          />
        ) : view === "reauth" ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <div className="font-medium">Confirm your password to continue</div>
            <div className="mt-2 space-y-2">
              <p className="text-foreground">
                Setting up two-factor authentication needs a recent sign-in. Enter your
                password to carry on — no need to sign out.
              </p>
              <Input
                type="password"
                autoComplete="current-password"
                aria-label="Password"
                value={reauthPassword}
                onChange={(e) => setReauthPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void reauthenticate();
                }}
              />
              {error ? <p className="break-words font-mono">{error.message}</p> : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => void reauthenticate()}
                  disabled={busy || !reauthPassword}
                >
                  {busy ? "Confirming…" : "Confirm and continue"}
                </Button>
                <Button type="button" variant="outline" onClick={() => void signOut()}>
                  Sign out instead
                </Button>
              </div>
            </div>
          </div>
        ) : view === "error" && error ? (
          <ErrorBox title="TOTP enrollment unavailable" error={error} />
        ) : view === "signed-out" ? (
          // generateSecret needs a signed-in user for the MFA session. Without
          // this branch the page sat on "Generating secret…" forever whenever
          // someone opened /mfa-enroll directly while signed out — no error,
          // no hint, nothing to act on.
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
            <div className="font-medium">You need to sign in first</div>
            <div className="mt-1">
              Enrolment needs a signed-in session.{" "}
              <a href="/login" className="underline">
                Sign in
              </a>
              , then come back to this page.
            </div>
          </div>
        ) : null}

        {view !== "unverified-email" ? (
          <>
            <div className="space-y-2">
              <label htmlFor="code" className="text-sm font-medium">
                6-digit code
              </label>
              <Input
                id="code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={!secret}
              />
            </div>
            <Button
              onClick={() => void verify()}
              disabled={busy || code.length !== 6 || !secret}
              className="w-full"
            >
              {busy ? "Verifying…" : "Verify and enrol"}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

// Copy the subtitle promised only what the current card can deliver: the old
// "Scan this URI" showed above the password prompt, the sign-in prompt, and
// the error card alike.
const SUBTITLE: Record<EnrolView, string> = {
  checking: "Checking your sign-in…",
  "signed-out": "Sign in to set up two-factor authentication.",
  ready: "Scan the QR code in an authenticator app, then enter the 6-digit code.",
  "unverified-email": "Verify your email address to continue.",
  reauth: "Confirm your password to generate a setup code.",
  error: "Two-factor enrolment is not available right now.",
  generating: "Preparing your setup code…",
};

function ErrorBox({ title, error }: { title: string; error: EnrollError }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
      <div className="font-medium">{title}</div>
      <div className="mt-1 break-words font-mono">
        {error.code ? `[${error.code}] ` : ""}
        {error.message}
      </div>
    </div>
  );
}

function UnverifiedEmailCard({
  email,
  verificationSent,
  verificationSending,
  resendError,
  onResend,
  onSignOut,
}: {
  email: string;
  verificationSent: boolean;
  verificationSending: boolean;
  resendError: string | null;
  onResend: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-warning/40 bg-warning/5 p-4 text-sm">
      <div className="font-medium text-foreground">Verify your email first</div>
      <p className="text-muted-foreground">
        Two-factor authentication can only be enrolled after the email on your account
        ({email || "your account"}) has been verified.
      </p>
      {verificationSent ? (
        <div className="rounded-md border border-success/40 bg-success/5 p-3 text-xs text-success">
          Verification email sent. Click the link, then sign out and back in here to retry.
        </div>
      ) : null}
      {resendError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          Could not send the email: {resendError}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          onClick={onResend}
          disabled={verificationSending || verificationSent}
        >
          {verificationSending
            ? "Sending…"
            : verificationSent
              ? "Sent"
              : "Send verification email"}
        </Button>
        <Button size="sm" variant="outline" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
